// public/sort-logic.js
// Pure functions for card sorting - no side effects, easily testable

import {
  CARDS_PER_PAGE_FACE,
  COLOR_CATEGORY_VALUES,
  COLOR_TAB_DISPLAY_NAMES,
  LAND_SORT_VALUES,
  REVISED_NON_DECK_TYPES,
} from './config.js';
import { getCardDisplayName } from './utils.js';

/**
 * Gets sort values for a card based on color category
 * @param {Object} cardInfo - The card info object
 * @returns {Object} - Sort values { category, isLand, landType, name, releaseDate }
 */
export function getSortColorValue(cardInfo) {
  const defaultName = cardInfo?.originalLineRef || cardInfo?.identifier?.name || 'zzzz';
  const defaultDate = '9999-12-31';

  if (!cardInfo || cardInfo.status !== 'found' || !cardInfo.cardData) {
    return {
      category: COLOR_CATEGORY_VALUES['NotFound'],
      isLand: true,
      landType: LAND_SORT_VALUES['Other'],
      name: defaultName.toLowerCase(),
      releaseDate: defaultDate,
    };
  }

  const cardData = cardInfo.cardData;
  const typeLine = cardData.type_line?.toLowerCase() || '';
  const colorIdentity = cardData.color_identity || [];
  const name = getCardDisplayName(cardData, defaultName);
  const nameLower = name.toLowerCase();
  const releaseDate = cardData.released_at || defaultDate;

  const isLand = typeLine.includes('land');
  const isBasic = typeLine.includes('basic');
  const isSnow = typeLine.includes('snow');
  const isNonDeckType = REVISED_NON_DECK_TYPES.some((type) => {
    const regex = new RegExp(`\\b${type}\\b`);
    return regex.test(typeLine);
  });

  let primaryCategory = COLOR_CATEGORY_VALUES['NotFound'];
  let landType = LAND_SORT_VALUES['Other'];

  if (isNonDeckType) {
    primaryCategory = COLOR_CATEGORY_VALUES['NonDeck'];
  } else if (isLand) {
    primaryCategory = COLOR_CATEGORY_VALUES['Land'];
    if (isBasic && isSnow) landType = LAND_SORT_VALUES['SnowBasic'];
    else if (isBasic) landType = LAND_SORT_VALUES['Basic'];
    else landType = LAND_SORT_VALUES['NonBasic'];
  } else if (colorIdentity.length > 1) {
    primaryCategory = COLOR_CATEGORY_VALUES['Multicolor'];
  } else if (colorIdentity.length === 1) {
    primaryCategory = COLOR_CATEGORY_VALUES[colorIdentity[0]] ?? COLOR_CATEGORY_VALUES['NotFound'];
  } else {
    if (typeLine.includes('artifact') && !typeLine.includes('creature')) {
      primaryCategory = COLOR_CATEGORY_VALUES['Artifact'];
    } else {
      primaryCategory = COLOR_CATEGORY_VALUES['Colorless'];
    }
  }

  return {
    category: primaryCategory,
    isLand: isLand,
    landType: landType,
    name: nameLower,
    releaseDate: releaseDate,
  };
}

/**
 * Comparator function for sorting cards by price (high to low)
 * @param {Object} a - First card info
 * @param {Object} b - Second card info
 * @returns {number} - Sort order
 */
export function comparePriceDescending(a, b) {
  const priceDiff = (b.price || 0) - (a.price || 0);
  if (priceDiff !== 0) return priceDiff;
  const nameA = a.cardData?.name || a.originalLineRef || '';
  const nameB = b.cardData?.name || b.originalLineRef || '';
  return nameA.localeCompare(nameB);
}

/**
 * Comparator function for sorting cards by color category
 * @param {Object} a - First card info
 * @param {Object} b - Second card info
 * @returns {number} - Sort order
 */
export function compareByColor(a, b) {
  const valA = getSortColorValue(a);
  const valB = getSortColorValue(b);

  const categoryDiff = valA.category - valB.category;
  if (categoryDiff !== 0) return categoryDiff;

  if (valA.isLand) {
    const landTypeDiff = valA.landType - valB.landType;
    if (landTypeDiff !== 0) return landTypeDiff;
    const dateDiff = valA.releaseDate.localeCompare(valB.releaseDate);
    if (dateDiff !== 0) return dateDiff;
    return valA.name.localeCompare(valB.name);
  } else {
    const nameDiff = valA.name.localeCompare(valB.name);
    if (nameDiff !== 0) return nameDiff;
    return valA.releaseDate.localeCompare(valB.releaseDate);
  }
}

/**
 * Sorts an array of cards by the specified mode
 * Returns a new sorted array (does not mutate input)
 * @param {Array} cards - Array of card info objects
 * @param {string} mode - 'price' or 'color'
 * @returns {Array} - New sorted array
 */
export function sortCards(cards, mode) {
  if (!Array.isArray(cards)) return [];
  if (!['price', 'color'].includes(mode)) return [...cards];

  const sorted = [...cards];
  if (mode === 'price') {
    sorted.sort(comparePriceDescending);
  } else if (mode === 'color') {
    sorted.sort(compareByColor);
  }
  return sorted;
}

/**
 * Converts backend-provided color offsets to color boundaries.
 * Maps short keys (M, C) to frontend keys (Multicolor, Colorless).
 * @param {Array<{key: string, offset: number}>} colorOffsets - From API response
 * @returns {Array<{key: string, label: string, firstCardIndex: number, totalCards: number}>}
 */
export function computeColorBoundariesFromOffsets(colorOffsets) {
  if (!Array.isArray(colorOffsets) || colorOffsets.length === 0) return [];

  // Map short backend keys to frontend keys
  const keyMap = { M: 'Multicolor', C: 'Colorless' };

  return colorOffsets.map((entry) => {
    const frontendKey = keyMap[entry.key] || entry.key;
    return {
      key: frontendKey,
      label: COLOR_TAB_DISPLAY_NAMES[frontendKey] || frontendKey,
      firstCardIndex: entry.offset,
      totalCards: 0, // not needed for tab navigation
    };
  });
}

/**
 * Scans a color-sorted card array and returns the first card index for each color category.
 * Only includes categories that have at least one card.
 * @param {Array} allCardsData - Cards in color-sort order
 * @returns {Array<{key: string, label: string, firstCardIndex: number, totalCards: number}>}
 */
export function computeColorBoundaries(allCardsData) {
  if (!Array.isArray(allCardsData) || allCardsData.length === 0) return [];

  // Tab order — Artifact is merged into Colorless (same tab)
  const tabOrder = ['W', 'U', 'B', 'R', 'G', 'Multicolor', 'Colorless'];
  // Categories that map to Colorless tab
  const colorlessCats = [COLOR_CATEGORY_VALUES['Artifact'], COLOR_CATEGORY_VALUES['Colorless']];

  const seen = new Map(); // tab key -> firstCardIndex

  for (let i = 0; i < allCardsData.length; i++) {
    const { category } = getSortColorValue(allCardsData[i]);
    // Map Artifact to Colorless tab
    const tabKey = colorlessCats.includes(category) ? 'Colorless' : null;

    if (tabKey && !seen.has(tabKey)) {
      seen.set(tabKey, i);
    } else {
      // Find the key for this category value
      for (const key of tabOrder) {
        if (COLOR_CATEGORY_VALUES[key] === category && !seen.has(key)) {
          seen.set(key, i);
          break;
        }
      }
    }
  }

  const totalCards = allCardsData.length;
  const boundaries = [];
  for (const key of tabOrder) {
    if (seen.has(key)) {
      boundaries.push({
        key,
        label: COLOR_TAB_DISPLAY_NAMES[key] || key,
        firstCardIndex: seen.get(key),
        totalCards,
      });
    }
  }
  return boundaries;
}

/**
 * Converts a card array index to the paperIndex that contains it.
 * Desktop: card 0-8 → paper 0 (back), card 9-17 → paper 1 (front), card 18-26 → paper 1 (back), etc.
 * Mobile: card 0-8 → paper 0, card 9-17 → paper 1, etc.
 * @param {number} cardIndex
 * @param {boolean} isMobileView
 * @returns {number}
 */
export function cardIndexToPaperIndex(cardIndex, isMobileView) {
  if (isMobileView) {
    return Math.floor(cardIndex / CARDS_PER_PAGE_FACE);
  }
  // Desktop: paper 0 back = cards 0-8, paper 1 front = 9-17, paper 1 back = 18-26, etc.
  if (cardIndex < CARDS_PER_PAGE_FACE) return 0;
  return Math.floor((cardIndex - CARDS_PER_PAGE_FACE) / (CARDS_PER_PAGE_FACE * 2)) + 1;
}

/**
 * Converts a card array index to a binder location (paper index).
 * Desktop: location 1 shows cards 0-17 (paper 0 back + paper 1 front).
 * Mobile: location 0 shows cards 0-8.
 * @param {number} cardIndex
 * @param {boolean} isMobileView
 * @returns {number}
 */
export function cardIndexToLocation(cardIndex, isMobileView) {
  if (isMobileView) {
    return Math.floor(cardIndex / CARDS_PER_PAGE_FACE);
  }
  // Desktop: 18 cards visible per location (two faces)
  // Location 1 = cards 0-17, location 2 = cards 18-35, etc.
  return Math.floor(cardIndex / (CARDS_PER_PAGE_FACE * 2)) + 1;
}

/**
 * Determines which color tab should be active based on current location.
 * Returns the key of the last boundary whose location <= currentLocation.
 * @param {Array<{key: string, firstCardIndex: number}>} boundaries
 * @param {number} currentLocation
 * @param {boolean} isMobileView
 * @returns {string|null}
 */
export function getActiveColorTab(boundaries, currentLocation, isMobileView) {
  if (!boundaries || boundaries.length === 0) return null;

  let activeKey = boundaries[0].key;
  for (const boundary of boundaries) {
    const loc = cardIndexToLocation(boundary.firstCardIndex, isMobileView);
    if (loc <= currentLocation) {
      activeKey = boundary.key;
    } else {
      break;
    }
  }
  return activeKey;
}
